import type { OperationsAttentionKey, OperationsItemExplanationDto, OperationsPendingActionDto } from "@/api/endpoints";
import { actorRoleLabel } from "@/features/operations/operationsLabels";
import { fmtETDate } from "@/lib/format";

/** What the banner says about a request. Built from the explain endpoint; null when there is nothing to show. */
export interface AttentionSummary {
  state: Exclude<OperationsAttentionKey, "clear" | "unknown">;
  /** "Stuck" / "Blocked" / "Stuck + Blocked". */
  label: string;
  /** The two clocks (Rafael, 2026-10-21). */
  isStuck: boolean;
  isBlocked: boolean;
  /** Whole business days since the last event on the request, and that event's date (yyyy-MM-dd). */
  untouchedBd: number;
  lastTouched: string;
  /** Whole business days since the request last moved state; null when nothing carries a state (no task pending, no work order). */
  sinceStateBd: number | null;
  stuckAfterBd: number;
  blockedAfterBd: number;
  /** Whole business days the deciding task (or the work order) has sat; null when nothing is pending. */
  businessDays: number | null;
  /** "task" when a task carries the state clock, "workOrder" when the unapproved work order does, "none" when nothing is pending. */
  cause: "task" | "workOrder" | "none";
  /** The deciding task, when the cause is a task. */
  task: { id: string | null; name: string; holder: string; roleLabel: string; openIndex: number; openCount: number } | null;
  /** The work order's step, when the cause is the work order: "to submit" / "to approve". */
  workOrderStep: string | null;
  /** "Client Service · Sara Nassif" / "Work order · to approve" / "nothing pending". */
  pendingWith: string;
  /** yyyy-MM-dd of the deciding assignment or the work-order anchor; null when nothing is pending. */
  since: string | null;
  /** Which anchor the work-order clock counts from, in words ("submitted", "request completed", "last task completed"). */
  sinceAnchor: string | null;
  /** "Stuck · 63 bd" / "Clear" as of the previous business day. */
  yesterday: string;
  /** "18 more tasks are queued behind it (Consultant ×16, Client Service ×2)." or the work-order line. */
  queueLine: string;
  /** Task ids queued behind the deciding one, in FBO order (for the Tasks table markers). */
  queuedTaskIds: string[];
}

export const ATTENTION_STATE_LABEL: Record<Exclude<OperationsAttentionKey, "clear" | "unknown">, string> = {
  stuck: "Stuck",
  blocked: "Blocked",
  stuckAndBlocked: "Stuck + Blocked",
};

function holderOf(action: OperationsPendingActionDto): string {
  const role = action.role === "Other" && action.roleRaw ? action.roleRaw : actorRoleLabel(action.role);
  return action.actorName ? `${role} · ${action.actorName}` : role;
}

function roleCounts(actions: OperationsPendingActionDto[]): string {
  const counts = new Map<string, number>();
  for (const a of actions) {
    const role = a.role === "Other" && a.roleRaw ? a.roleRaw : actorRoleLabel(a.role);
    counts.set(role, (counts.get(role) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
    .map(([role, n]) => `${role} ×${n}`)
    .join(", ");
}

function yesterdayLabel(explanation: OperationsItemExplanationDto): string {
  const prev = explanation.previous;
  const state = prev.attention[0];
  if (!state) return "not open";
  const label = state === "clear" ? "Clear" : state === "unknown" ? "Unknown" : ATTENTION_STATE_LABEL[state];
  return prev.pendingForBd != null && state !== "clear" && state !== "unknown" ? `${label} · ${prev.pendingForBd} bd` : label;
}

function isFlagged(state: OperationsAttentionKey | undefined): state is "stuck" | "blocked" | "stuckAndBlocked" {
  return state === "stuck" || state === "blocked" || state === "stuckAndBlocked";
}

/**
 * Turns the explain payload into the banner's facts. Null when the request is closed, Clear, or
 * its activity is unknown: the banner only exists to explain a flag.
 */
export function attentionSummary(explanation: OperationsItemExplanationDto | null | undefined): AttentionSummary | null {
  if (!explanation) return null;
  const { item, pending, stuckAfterBd, blockedAfterBd } = explanation;
  const state = item.attention[0];
  if (!isFlagged(state) || item.untouchedBd == null || !item.lastTouchedAtUtc) return null;
  const waiting = item.waitingOn;
  const common = {
    state,
    label: ATTENTION_STATE_LABEL[state],
    isStuck: state !== "blocked",
    isBlocked: state !== "stuck",
    untouchedBd: item.untouchedBd,
    lastTouched: fmtETDate(item.lastTouchedAtUtc),
    sinceStateBd: item.sinceStateBd,
    stuckAfterBd,
    blockedAfterBd,
    yesterday: yesterdayLabel(explanation),
  };

  if (!waiting) {
    return {
      ...common,
      businessDays: null,
      cause: "none",
      task: null,
      workOrderStep: null,
      pendingWith: "nothing pending",
      since: null,
      sinceAnchor: null,
      queueLine: "No task is pending and no work order is waiting: only the request's own events count.",
      queuedTaskIds: [],
    };
  }

  if (waiting.kind === "workOrder") {
    const anchor =
      explanation.workOrderLastActionAtUtc && waiting.sinceUtc === explanation.workOrderLastActionAtUtc
        ? "last work-order action"
        : explanation.workOrderSubmittedAtUtc && waiting.sinceUtc === explanation.workOrderSubmittedAtUtc
          ? "submitted"
          : explanation.requestCompletedAtUtc && waiting.sinceUtc === explanation.requestCompletedAtUtc
            ? "request completed"
            : "last task completed";
    return {
      ...common,
      businessDays: waiting.businessDays,
      cause: "workOrder",
      task: null,
      workOrderStep: waiting.detail,
      pendingWith: `Work order · ${waiting.detail ?? ""}`.trim(),
      since: fmtETDate(waiting.sinceUtc),
      sinceAnchor: anchor,
      queueLine: "No task is pending; every task is done.",
      queuedTaskIds: [],
    };
  }

  const head = pending[0];
  const queued = pending.slice(1);
  const name = waiting.taskName ?? head?.taskName ?? "the open task";
  return {
    ...common,
    businessDays: waiting.businessDays,
    cause: "task",
    task: {
      id: waiting.taskId ?? head?.taskId ?? null,
      name,
      holder: waiting.detail ?? head?.actorName ?? "",
      roleLabel: waiting.role === "Other" && waiting.roleRaw ? waiting.roleRaw : actorRoleLabel(waiting.role ?? ""),
      openIndex: 1,
      openCount: pending.length,
    },
    workOrderStep: null,
    pendingWith: head ? holderOf(head) : `${actorRoleLabel(waiting.role ?? "")}${waiting.detail ? ` · ${waiting.detail}` : ""}`,
    since: fmtETDate(waiting.sinceUtc),
    sinceAnchor: null,
    queueLine:
      queued.length === 0
        ? "No other task is open."
        : `${queued.length} more ${queued.length === 1 ? "task is" : "tasks are"} queued behind it (${roleCounts(queued)}).`,
    queuedTaskIds: queued.map((a) => a.taskId).filter((id): id is string => !!id),
  };
}
